import { useId, useState } from 'react'
import Image from 'next/image'
import cn from 'classnames'
import { useTranslation } from '@mc/i18n'
import { cloudflareLoader } from '@mc/image-cloudflare'
import { TrackClick } from 'pages/certificates/tracking'
import PeopleIcon from '../assets/people-highlight.svg'
import { ResponsiveText } from '../hero/ResponsiveText'
import type { CohortInstructor } from '../types'

export const CohortInstructorCard = ({
  instructor,
}: {
  instructor: CohortInstructor
}) => {
  const { t } = useTranslation('@mc/certificates')
  const [isExpanded, setIsExpanded] = useState(false)
  const bioId = useId()
  const toggleLabel = isExpanded
    ? t('marketingPages.cohorts.instructors.closeBio')
    : t('marketingPages.cohorts.instructors.viewBio')
  const { name, description, imageUrl, cardTitle, role, summary, highlight } =
    instructor

  return (
    <article className='relative overflow-hidden rounded-xl bg-c-base-1400 text-c-text-light'>
      {imageUrl && (
        <div className='relative aspect-square lg:absolute lg:inset-y-0 lg:left-0 lg:aspect-auto lg:w-1/2'>
          <Image
            src={imageUrl}
            alt=''
            fill
            loader={cloudflareLoader}
            sizes='(min-width: 960px) 50vw, 100vw'
            className='object-cover'
          />
          <div className='absolute inset-0 bg-gradient-to-b from-transparent to-c-base-1400 lg:bg-gradient-to-r' />
        </div>
      )}

      <div
        className={cn(
          'relative flex flex-col gap-6 px-5 pb-10 lg:ml-auto lg:w-1/2 lg:py-8 lg:pl-0 lg:pr-9',
          { '-mt-10 lg:mt-0': imageUrl },
        )}
      >
        <div className='flex flex-col gap-4'>
          <h3 className='c-heading-3 m-0'>
            <ResponsiveText
              value={
                isExpanded ? { mobile: cardTitle, desktop: name } : cardTitle
              }
            />
          </h3>
          <p className='c-heading-6 m-0'>{role}</p>
        </div>

        {/* Both states share one grid cell so the card keeps the taller one's height. */}
        <div className='grid'>
          <div
            className={cn('col-start-1 row-start-1 flex flex-col gap-6 p-0', {
              invisible: isExpanded,
            })}
          >
            <p className='c-body m-0'>{summary}</p>
            {highlight && (
              <div className='flex items-center gap-4'>
                <span
                  aria-hidden
                  className='flex shrink-0 rounded-sm bg-c-brand-highlight/10 p-3 lg:p-4'
                >
                  <PeopleIcon />
                </span>
                <div className='flex flex-col gap-1'>
                  <p className='c-body-bold m-0 text-c-brand-highlight'>
                    {highlight.title}
                  </p>
                  <p className='c-body m-0 text-c-text-tint-1'>
                    {highlight.subtitle}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div
            id={bioId}
            className={cn('col-start-1 row-start-1 p-0', {
              invisible: !isExpanded,
            })}
          >
            <p className='c-body m-0'>{description}</p>
          </div>
        </div>
      </div>

      <TrackClick
        event='certificateLandingClick'
        action={name}
        enabled={!isExpanded}
      >
        <button
          type='button'
          aria-expanded={isExpanded}
          aria-controls={bioId}
          aria-label={`${toggleLabel}, ${name}`}
          className={cn(
            'c-body-bold absolute bottom-5 right-5 cursor-pointer rounded-full border-2 border-c-brand-highlight px-4 py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-c-brand-highlight lg:bottom-6 lg:right-6',
            isExpanded
              ? 'bg-transparent text-c-brand-highlight'
              : 'bg-c-brand-highlight text-c-text-dark',
          )}
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          {toggleLabel}
        </button>
      </TrackClick>
    </article>
  )
}
